import { create } from "zustand";
import {
  PDA1130_R01Dto,
  PDA1130_R01SearchParams,
  PDA1130_R01DetailDto,
  PDA1130_R01LotDto,
} from "./PDA1130_R01Dto";

// 검수된 항목 타입
export interface InspectedItem {
  lotNo: string;
  itemCd: string;
  itemNm: string;
  shipQty: number;
  warehouseCd: string;
  locationCd: string;
}

export interface PDA1130_R01Store {
  // 목록 화면 상태
  shippingOrderList: PDA1130_R01Dto[];
  loading: boolean;
  searchParams: PDA1130_R01SearchParams;
  
  // 상세 화면 상태
  showList: boolean;  // true: 목록, false: 상세
  selectedShippingOrder: PDA1130_R01Dto | null;
  shippingDetail: PDA1130_R01DetailDto | null;
  scannedLot: PDA1130_R01LotDto | null;
  lotNo: string;
  
  // LOT 목록 상태 (v1.1)
  lotList: PDA1130_R01LotDto[];  // 품목별 LOT 목록
  showLotModal: boolean;  // LOT 선택 모달 표시 여부
  selectedItemForLot: string | null;  // LOT 조회할 품목코드
  
  // 검수 상태
  inspectedItems: InspectedItem[];  // 검수된 항목들
  
  // 목록 화면 Actions
  setShippingOrderList: (list: PDA1130_R01Dto[]) => void;
  setLoading: (loading: boolean) => void;
  setSearchParams: (params: PDA1130_R01SearchParams) => void;
  updateSearchParam: (field: keyof PDA1130_R01SearchParams, value: string) => void;
  resetSearchParams: () => void;
  
  // 상세 화면 Actions
  setShowList: (show: boolean) => void;
  setSelectedShippingOrder: (order: PDA1130_R01Dto | null) => void;
  setShippingDetail: (detail: PDA1130_R01DetailDto | null) => void;
  setScannedLot: (lot: PDA1130_R01LotDto | null) => void;
  setLotNo: (lotNo: string) => void;
  resetDetail: () => void;
  
  // LOT 목록 Actions (v1.1)
  setLotList: (list: PDA1130_R01LotDto[]) => void;
  setShowLotModal: (show: boolean) => void;
  setSelectedItemForLot: (itemCd: string | null) => void;
  
  // 검수 Actions
  addInspectedItem: (item: InspectedItem) => void;
  removeInspectedItem: (lotNo: string) => void;
  clearInspectedItems: () => void;
  
  // 전체 초기화
  reset: () => void;
}
const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

const initialSearchParams: PDA1130_R01SearchParams = {
  orderNo: "",
    startDate: formatLocalDate(firstDay),  //   "2026-02-01"
    endDate: formatLocalDate(today),       //   "2026-02-02"
};

const usePDA1130_R01Store = create<PDA1130_R01Store>((set) => ({
  // 목록 화면 초기값
  shippingOrderList: [],
  loading: false,
  searchParams: initialSearchParams,
  
  // 상세 화면 초기값
  showList: true,
  selectedShippingOrder: null,
  shippingDetail: null,
  scannedLot: null,
  lotNo: "",
  
  // LOT 목록 초기값 (v1.1)
  lotList: [],
  showLotModal: false,
  selectedItemForLot: null,
  
  // 검수 초기값
  inspectedItems: [],

  // 목록 화면 Actions
  setShippingOrderList: (list) => set({ shippingOrderList: list }),
  setLoading: (loading) => set({ loading }),
  setSearchParams: (params) => set({ searchParams: params }),
  
  updateSearchParam: (field, value) =>
    set((state) => ({
      searchParams: {
        ...state.searchParams,
        [field]: value,
      },
    })),
  
  resetSearchParams: () => set({ searchParams: initialSearchParams }),
  
  // 상세 화면 Actions
  setShowList: (show) => set({ showList: show }),
  setSelectedShippingOrder: (order) => set({ selectedShippingOrder: order }),
  setShippingDetail: (detail) => set({ shippingDetail: detail }),
  setScannedLot: (lot) => set({ scannedLot: lot }),
  setLotNo: (lotNo) => set({ lotNo }),
  
  resetDetail: () => set({
    selectedShippingOrder: null,
    shippingDetail: null,
    scannedLot: null,
    lotNo: "",
    lotList: [],
    showLotModal: false,
    selectedItemForLot: null,
    inspectedItems: [],
  }),
  
  // LOT 목록 Actions (v1.1)
  setLotList: (list) => set({ lotList: list }),
  setShowLotModal: (show) => set({ showLotModal: show }),
  setSelectedItemForLot: (itemCd) => set({ selectedItemForLot: itemCd }),
  
  // 검수 Actions
  addInspectedItem: (item) =>
    set((state) => ({
      inspectedItems: [...state.inspectedItems, item],
    })),
  
  removeInspectedItem: (lotNo) =>
    set((state) => ({
      inspectedItems: state.inspectedItems.filter((item) => item.lotNo !== lotNo),
    })),
  
  clearInspectedItems: () => set({ inspectedItems: [] }),
  
  // 전체 초기화
  reset: () =>
    set({
      shippingOrderList: [],
      loading: false,
      searchParams: initialSearchParams,
      showList: true,
      selectedShippingOrder: null,
      shippingDetail: null,
      scannedLot: null,
      lotNo: "",
      lotList: [],
      showLotModal: false,
      selectedItemForLot: null,
      inspectedItems: [],
    }),
}));

export default usePDA1130_R01Store;